/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useEffect, useRef, useState } from "react";
import { Button } from "@vector-im/compound-web";

import { FieldRow } from "../input/Input";
import { useSetting } from "./settings";
import { cachedMediaBytes, lastBackupAt } from "../meet/MeetSettings";
import { downloadBackup, importBackup } from "../meet/features/data/useDataBackup";
import styles from "./MeetSettings.module.css";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export const DataSettingsTab: FC = () => {
  const [cached, setCached] = useSetting(cachedMediaBytes);
  const [lastBackup, setLastBackup] = useSetting(lastBackupAt);
  const [message, setMessage] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (navigator.storage?.estimate) {
      navigator.storage
        .estimate()
        .then((e) => setCached(e.usage ?? 0))
        .catch(() => {});
    }
  }, [setCached]);

  return (
    <div>
      <h3 className={styles.sectionTitle}>Local Data Export / Import</h3>
      <p className={styles.small}>
        Export all settings, preferences and configs to a local JSON file and
        import them back on any device. 100% local.
      </p>
      <Button onClick={() => downloadBackup()}>Export settings</Button>{" "}
      <Button
        onClick={() => {
          inputRef.current?.click();
        }}
      >
        Import settings
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="application/json"
        style={{ display: "none" }}
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          const res = importBackup(await f.text());
          setMessage(res.message);
        }}
      />
      {message && <p className={styles.small}>{message}</p>}

      <h3 className={styles.sectionTitle}>Backup & Restore</h3>
      <Button
        onClick={() => {
          downloadBackup();
          setLastBackup(Date.now());
        }}
      >
        One-click full backup
      </Button>
      <p className={styles.small}>
        {lastBackup
          ? `Last backup: ${new Date(lastBackup).toLocaleString()}`
          : "No backup taken yet."}
      </p>

      <h3 className={styles.sectionTitle}>Cache Management</h3>
      <p className={styles.small}>
        Local data currently using: {formatBytes(cached)}
      </p>
      <Button
        onClick={async () => {
          try {
            if (navigator.storage) {
              const keys = await caches.keys();
              await Promise.all(keys.map((k) => caches.delete(k)));
            }
          } catch {
            /* best-effort */
          }
          if (navigator.storage?.estimate) {
            const e = await navigator.storage.estimate();
            setCached(e.usage ?? 0);
          }
          setMessage("Local caches cleared.");
        }}
      >
        Clear local caches
      </Button>
      <FieldRow>{message && <p className={styles.small}>{message}</p>}</FieldRow>
    </div>
  );
};
